import { ArrowRight, Shield, FileText as FileCheck, Warning as AlertTriangle, Flag } from '@phosphor-icons/react';
import { Helmet } from 'react-helmet-async';
import { Link } from '@/lib/router-compat';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { useRoOnlyGuard } from '@/hooks/useRoOnlyGuard';


const trustPages = [
  {
    title: 'Verificare Dealeri',
    description: 'Cum verificăm fiecare dealer: date de firmă, activitate, badge „Dealer Verificat".',
    href: '/incredere/verificare-dealeri',
    icon: Shield,
  },
  {
    title: 'Reguli de Publicare',
    description: 'Ce este permis și ce nu pe platformă. Reguli clare pentru anunțuri corecte.',
    href: '/incredere/reguli-publicare',
    icon: FileCheck,
  },
  {
    title: 'Politica Anti-Fraudă',
    description: 'Cum prevenim și detectăm frauda. Mecanisme de protecție pentru cumpărători.',
    href: '/incredere/anti-frauda',
    icon: AlertTriangle,
  },
  {
    title: 'Raportează o Listare',
    description: 'Ai văzut un anunț suspect? Raportează-l și echipa noastră va investiga.',
    href: '/incredere/raporteaza-listare',
    icon: Flag,
  },
];

export default function TrustHub() {
  const __roBlocked = useRoOnlyGuard();
  const canonicalUrl = 'https://roautomarket.ro/incredere';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Trust Center | Siguranță și Încredere – RO Auto Market</title>
        <meta name="description" content="Centrul de Încredere RO Auto Market: cum verificăm dealerii, regulile platformei, politica anti-fraudă și cum raportezi o listare suspectă." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Trust Center – RO Auto Market" />
        <meta property="og:description" content="Siguranță și transparență pe RO Auto Market." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://roautomarket.ro/og-roautomarket.png" />
      </Helmet>

      <section className="surface-dark pt-4 pb-6 md:py-10 px-4">
        <div className="container mx-auto max-w-5xl">
          <Breadcrumbs items={[{ label: 'Trust Center', href: '/incredere' }]} className="mb-6" />
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-primary" />
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">Trust Center</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Siguranța ta este prioritatea noastră. Aici găsești toate informațiile despre cum protejăm cumpărătorii și cum verificăm dealerii de pe platformă.
          </p>
        </div>
      </section>

      <section className="py-12 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-6">
            {trustPages.map((page) => {
              const Icon = page.icon;
              return (
                <Card key={page.href} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-foreground mb-2">{page.title}</h2>
                        <p className="text-sm text-muted-foreground mb-3">{page.description}</p>
                        <Link to={page.href} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                          Citește mai mult <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 p-6 rounded-lg bg-muted/50">
            <h3 className="font-semibold text-foreground mb-3">Resurse conexe</h3>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/ghiduri" className="text-primary hover:underline">Ghiduri auto</Link>
              <Link to="/dealeri-verificati" className="text-primary hover:underline">Dealeri verificați</Link>
              <Link to="/vehicles" className="text-primary hover:underline">Toate vehiculele</Link>
              <Link to="/contact" className="text-primary hover:underline">Contact</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
